import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import { Box, Typography, IconButton, Tooltip, Button } from "@mui/material";
import CustomTable, { CustomTableColumn } from "components/common/CustomTable";
import { BranchDto } from "libs/cellera-sdk";
import { useState, useCallback } from "react";

interface PracticeBranchesViewProps {
  readonly branches: BranchDto[];
  readonly onUpdateBranch?: (branch: BranchDto) => void;
  readonly onDeleteBranch?: (branch: BranchDto) => void;
  readonly onStaffCountClick?: (branch: BranchDto) => void;
  readonly onAddBranch?: () => void; // New prop for add branch button
}

interface BranchActionsProps {
  readonly branch: BranchDto;
  readonly onUpdate?: (branch: BranchDto) => void;
  readonly onDelete?: (branch: BranchDto) => void;
}

function BranchActions({ branch, onUpdate, onDelete }: BranchActionsProps) {
  return (
    <Box className="flex gap-1">
      {onUpdate && (
        <Tooltip title="Update Branch">
          <IconButton
            size="small"
            onClick={() => onUpdate(branch)}
            color="primary"
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {onDelete && (
        <Tooltip title="Delete Branch">
          <IconButton
            size="small"
            onClick={() => onDelete(branch)}
            color="error"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

interface StaffCountLinkProps {
  readonly branch: BranchDto;
  readonly onStaffCountClick?: (branch: BranchDto) => void;
}

function StaffCountLink({ branch, onStaffCountClick }: StaffCountLinkProps) {
  return onStaffCountClick ? (
    <Box
      component="button"
      onClick={() => onStaffCountClick(branch)}
      sx={{
        cursor: "pointer",
        textDecoration: "underline",
        color: "primary.main",
        backgroundColor: "transparent",
        border: "none",
        padding: 0,
        fontSize: "inherit",
        "&:hover": {
          color: "primary.dark",
        },
      }}
    >
      {branch.staffCount?.toString() || "0"}
    </Box>
  ) : (
    <>{branch.staffCount?.toString() || "0"}</>
  );
}

function PracticeBranchesView({
  branches,
  onUpdateBranch,
  onDeleteBranch,
  onStaffCountClick,
  onAddBranch,
}: PracticeBranchesViewProps) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const handleChangePage = useCallback((_: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    },
    []
  );

  const columns: CustomTableColumn<BranchDto>[] = [
    {
      id: "address",
      label: "Address",
      getValue: (branch) => branch.address || "-",
    },
    {
      id: "emailAddress",
      label: "Email Address",
      getValue: (branch) => branch.emailAddress || "-",
    },
    {
      id: "primaryPhoneNumber",
      label: "Primary Phone Number",
      getValue: (branch) => branch.primaryPhoneNumber || "-",
    },
    {
      id: "faxNumber",
      label: "Fax Number",
      getValue: (branch) => branch.faxNumber || "-",
    },
    {
      id: "staffCount",
      label: "Staff",
      getValue: (branch) => (
        <StaffCountLink branch={branch} onStaffCountClick={onStaffCountClick} />
      ),
    },
    ...(onUpdateBranch || onDeleteBranch
      ? [
          {
            id: "actions" as const,
            label: "Actions",
            getValue: (branch: BranchDto) => (
              <BranchActions
                branch={branch}
                onUpdate={onUpdateBranch}
                onDelete={onDeleteBranch}
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <Box className="space-y-6 mt-8">
      <Box className="flex justify-between items-center">
        <Typography variant="h6" className="text-xl font-medium text-gray-900">
          Branches
        </Typography>
        {onAddBranch && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onAddBranch}
          >
            Add a Branch
          </Button>
        )}
      </Box>

      <CustomTable
        data={branches}
        columns={columns}
        totalCount={branches.length}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[5, 10, 25]}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        isLoading={false}
        emptyStateContent={
          <Typography className="text-center py-8 text-gray-500">
            No branches found
          </Typography>
        }
      />
    </Box>
  );
}

export default PracticeBranchesView;
